import React, { useEffect, useRef, useState, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  TableContainer,
  TableContent,
  TableHeader,
  TABLE_LAYOUT_OPTIONS,
  usePreventTableScrollingRef,
  Loader,
  TextButton,
  Dialog,
  PrimaryButton,
  SecondaryButton,
} from "cdk-radial";
import classNames from "classnames";
import { BUNDLE_PLAN_TYPES, BUNDLE_STATUS, capitalizeVisibility, PLAN_TYPE, PLAN_VISIBILITY } from "../Utils/constants";
import config from "../config/app.conf.json";
import { BUNDLE_PRICE_PLAN_HISTORY, PRICING_LIST_PLANS } from "./screen";

const EllipsisCellRenderer = ({ value }) => (
  <div className="ellipsis_cell_renderer">{value || ""}</div>
);

// Helper function to get display name with fallback to groupName
// For advanced + private plans, prefer groupName over planName
export function getPlanDisplayName(plan) {
  if (plan?.planType === PLAN_TYPE.ADVANCED && plan?.visibility === PLAN_VISIBILITY.PRIVATE && plan?.groupName) {
    return plan.groupName;
  }
  return plan?.planName || plan?.groupName || "N/A";
}

export default function PricePlanList({
  pricePlans,
  onReloadData,
  onSharePlan,
  onEditPlan,
  auth,
  selectedBundle,
  setScreen,
  setPrevScreenForHistory,
  setPricePlanForHistory,
  setShowSuccessToast,
  setToastMessage,
}) {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showRetireDialog, setShowRetireDialog] = useState(false);
  const [showCannotDeleteDialog, setShowCannotDeleteDialog] = useState(false);
  const [showCannotDeletePublicDialog, setShowCannotDeletePublicDialog] = useState(false);
  const [showCannotDeleteSharedDialog, setShowCannotDeleteSharedDialog] = useState(false);
  const [showRetireErrorDialog, setShowRetireErrorDialog] = useState(false);
  const [planToRetire, setPlanToRetire] = useState(null);
  const [retireError, setRetireError] = useState("");
  const gridRef = useRef();
  const tableContentRef = usePreventTableScrollingRef();
  const { accessToken } = auth || {};

  // Common API calling function for reusability
  const makeApiCall = useCallback(async (url, options = {}) => {
    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return { success: true, response };
    } catch (error) {
      console.error("API call failed:", error);

      const errorMessage = error.message.includes('HTTP error! status:')
        ? `Server error: ${error.message.replace('HTTP error! status:', 'Status')}`
        : "Network error. Please check your connection and try again.";

      return { success: false, error: errorMessage };
    }
  }, [accessToken]);

  const initializeData = useCallback(() => {
    if (!pricePlans) {
      setIsLoading(true);
      return;
    }

    let dataToProcess = null;
    if (Array.isArray(pricePlans)) {
      dataToProcess = pricePlans;
    } else if (Array.isArray(pricePlans?.items)) {
      dataToProcess = pricePlans.items;
    }

    if (dataToProcess && dataToProcess.length > 0) {
      const activePlans = dataToProcess.filter(
        (plan) => !plan.status || plan.status === BUNDLE_STATUS.ACTIVE
      );
      const tableData = activePlans.map((plan) => {
        let formattedDate = "N/A";
        let rawDate = null;
        if (plan?.updated) {
          const date = new Date(plan.updated);
          rawDate = date;
          formattedDate = date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          });
        }
        return {
          plan,
          plan_name: getPlanDisplayName(plan),
          visibility: capitalizeVisibility(plan?.visibility),
          plan_type: BUNDLE_PLAN_TYPES[plan?.planType] || plan?.planType,
          updated_on: formattedDate,
          shared_with: plan?.bundlePricePlanShare?.length || "N/A",
          rawUpdatedDate: rawDate,
        };
      });

      const sortedTableData = tableData.sort((a, b) => {
        if (!a.rawUpdatedDate && !b.rawUpdatedDate) return 0;
        if (!a.rawUpdatedDate) return 1;
        if (!b.rawUpdatedDate) return -1;

        return new Date(b.rawUpdatedDate) - new Date(a.rawUpdatedDate);
      });

      const finalTableData = sortedTableData.map(
        ({ rawUpdatedDate, ...rest }) => rest
      );

      setData(finalTableData);
      setIsLoading(false);
    } else {
      setData([]);
      setIsLoading(false);
    }
  }, [pricePlans]);

  useEffect(() => {
    initializeData();
  }, [initializeData]);

  const handleReloadData = () => {
    setIsLoading(true);
    if (onReloadData) {
      onReloadData();
    }
  };

  const CustomNoRowsOverlay = () => (
    <div className="empty_no_rows_overlay">
      <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
        No pricing plans available
      </div>
    </div>
  );
  const viewPlanRenderer = ({ data }) => {
    const handleOpenHistoryView = () => {
      setPricePlanForHistory(data.plan);
      setPrevScreenForHistory(PRICING_LIST_PLANS);
      setScreen(BUNDLE_PRICE_PLAN_HISTORY);
    };

    return <TextButton text="View" onClick={handleOpenHistoryView} data-testid="view-button" />;
  };
  const retiredPlanRenderer = ({ data: rowData }) => {
    const handleOpenRetireDialog = () => {
      setPlanToRetire(rowData.plan);
      const activePlans = data.filter(item => item.plan && (!item.plan.status || item.plan.status === BUNDLE_STATUS.ACTIVE));
      if (activePlans.length <= 1 && selectedBundle?.status === BUNDLE_STATUS.ACTIVE) {
        setShowCannotDeleteDialog(true);
        return;
      }
      if (selectedBundle?.status === BUNDLE_STATUS.ACTIVE && rowData.plan.bundlePricePlanShare && rowData.plan.bundlePricePlanShare.length > 0) {
        setShowCannotDeleteSharedDialog(true);
        return;
      }
      if (selectedBundle?.status === BUNDLE_STATUS.ACTIVE && rowData.plan.visibility === "public") {
        setShowCannotDeletePublicDialog(true);
        return;
      }

      setShowRetireDialog(true);
    };

    return <TextButton text="Retire Plan" onClick={handleOpenRetireDialog} data-testid="retire-button" />;
  };

  const handleConfirmRetirePlan = async () => {
    if (!planToRetire) return;
    const bundleId = planToRetire.bundleId;
    const planId = planToRetire.id;
    const groupId = planToRetire?.groupId;
    let result;
    if (groupId) {
      result = await makeApiCall(
        `${config.api.api_gateway_url}/v1/admin/bundles/${bundleId}/price-plan-groups/${groupId}`,
        { method: "DELETE" },
      );
    } else {
      result = await makeApiCall(
        `${config.api.api_gateway_url}/v1/admin/bundles/${bundleId}/price-plans/${planId}`,
        { method: "DELETE" },
      );
    }

    if (result.success) {
      setShowRetireDialog(false);
      if (setShowSuccessToast) {
        setShowSuccessToast(true);
      }
      if (setToastMessage) {
        const message = (
          <div>
            The plan <strong>{getPlanDisplayName(planToRetire)}</strong> has
            been successfully retired.
          </div>
        );
        setToastMessage(message);
      }
      setPlanToRetire(null);
      if (onReloadData) onReloadData();
    } else {
      setShowRetireDialog(false);
      setRetireError(`Failed to delete pricing plan. ${result.error}`);
      setShowRetireErrorDialog(true);
    }
  };
  const sharedPlanRenderer = ({ data }) => {
    return (
      <TextButton
        text="Share"
        onClick={() => onSharePlan && onSharePlan(data.plan)}
        isDisabled={data.plan.visibility === "public"}
        data-testid="share-button"
      />
    );
  };
  const editPlanRenderer = ({ data }) => {
    return (
      <TextButton
        text="Edit"
        onClick={() => onEditPlan && onEditPlan(data.plan)}
        data-testid="edit-button"
      />
    );
  };

  const columns = () => [
    {
      cellRendererFramework: EllipsisCellRenderer,
      field: "plan_name",
      headerName: "Plan Name (internal use only)",
      minWidth: 150,
      width: 150,
      sortable: true,
    },
    {
      cellRendererFramework: EllipsisCellRenderer,
      field: "visibility",
      headerName: "Visibility",
      minWidth: 70,
      width: 100,
      sortable: true,
    },
    {
      cellRendererFramework: EllipsisCellRenderer,
      field: "plan_type",
      headerName: "Plan Type",
      minWidth: 70,
      width: 135,
      sortable: true,
      resizable: true,
    },
    {
      cellRendererFramework: EllipsisCellRenderer,
      field: "updated_on",
      headerName: "Updated On (MM/DD/YYYY)",
      minWidth: 70,
      width: 125,
      sortable: true,
    },
    {
      cellRendererFramework: EllipsisCellRenderer,
      field: "shared_with",
      headerName: "Shared With",
      minWidth: 70,
      width: 100,
      sortable: true,
    },
    {
      field: "history",
      headerName: "History",
      cellClass: "history-cell",
      cellRendererFramework: viewPlanRenderer,
      minWidth: 50,
      width: 100,
      sortable: true,
    },
    {
      field: "edit",
      headerName: "Edit",
      cellClass: "edit-cell",
      cellRendererFramework: editPlanRenderer,
      minWidth: 50,
      width: 100,
      sortable: true,
    },
    {
      field: "share",
      headerName: "Share",
      cellClass: "share-cell",
      cellRendererFramework: sharedPlanRenderer,
      minWidth: 50,
      width: 100,
      sortable: true,
    },
    {
      field: "retire_plan",
      headerName: "Retire Plan",
      cellClass: "retire-plan-cell",
      cellRendererFramework: retiredPlanRenderer,
      minWidth: 50,
      width: 100,
      sortable: true,
    },
  ];

  const handleGridReady = useCallback((gridParams) => {
    gridParams.api.sizeColumnsToFit();
    gridParams.api.refreshCells({ force: true });
  }, []);

  const headerProps = {
    dataTestId: `bundles-table-header`,
    headerTitle: "Pricing Plans",
    hideBorder: false,
    hideTableHeader: true,
    hideTableTitle: false,
    id: `contracted-bundles-table-header`,
    isAlwaysExpanded: false,
    isSearchable: false,
    isPrintable: false,
    isDownloadable: false,
    overflowButtonProps: undefined,
    customActions: (
      <TextButton
        text="Reload Data"
        onClick={handleReloadData}
        disabled={isLoading}
        data-testid="reload-pricing-data-btn"
        style={{ marginLeft: "10px" }}
      />
    ),
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
        }}
      >
        <Loader
          className="pricing-plans-loader"
          data-testid="pricing_plans_loader"
          label="Loading pricing plans..."
          variant="inline"
        />
      </div>
    );
  }

  return (
    <>
      <Dialog
        isOpen={showRetireDialog}
        onClose={() => {
          setShowRetireDialog(false);
          setPlanToRetire(null);
        }}
        title="Delete Pricing Plan"
      >
        <div style={{ marginBottom: "16px" }}>
          Are you sure you want to delete this pricing plan?
        </div>
        <div className="retire-plan-dialog-buttons">
          <PrimaryButton text="OK" onClick={handleConfirmRetirePlan} />
          <SecondaryButton
            text="CANCEL"
            onClick={() => {
              setShowRetireDialog(false);
              setPlanToRetire(null);
            }}
          />
        </div>
      </Dialog>
      <Dialog
        isOpen={showCannotDeleteDialog}
        onClose={() => {
          setShowCannotDeleteDialog(false);
          setPlanToRetire(null);
        }}
        title="Cannot delete pricing plan"
      >
        <div style={{ marginBottom: "16px" }}>
          A bundle must have at least one active pricing plan.
        </div>
        <div className="retire-plan-dialog-buttons" >
          <PrimaryButton
            text="OK"
            onClick={() => {
              setShowCannotDeleteDialog(false);
              setPlanToRetire(null);
            }}
          />
        </div>
      </Dialog>

      <Dialog
        isOpen={showCannotDeletePublicDialog}
        onClose={() => {
          setShowCannotDeletePublicDialog(false);
          setPlanToRetire(null);
        }}
        title="Cannot delete public pricing plan"
      >
        <div style={{ marginBottom: "16px" }}>
          You cannot delete public pricing plans while the associated bundle is active.
        </div>
        <div className="retire-plan-dialog-buttons" >
          <PrimaryButton
            text="OK"
            onClick={() => {
              setShowCannotDeletePublicDialog(false);
              setPlanToRetire(null);
            }}
          />
        </div>
      </Dialog>

      <Dialog
        isOpen={showCannotDeleteSharedDialog}
        onClose={() => {
          setShowCannotDeleteSharedDialog(false);
          setPlanToRetire(null);
        }}
        title="Cannot delete shared pricing plan"
      >
        <div style={{ marginBottom: "16px" }}>
          You cannot delete a pricing plan that is currently being shared with an organization.
        </div>
        <div className="retire-plan-dialog-buttons" >
          <PrimaryButton
            text="OK"
            onClick={() => {
              setShowCannotDeleteSharedDialog(false);
              setPlanToRetire(null);
            }}
          />
        </div>
      </Dialog>
      <Dialog
        isOpen={showRetireErrorDialog}
        onClose={() => {
          setShowRetireErrorDialog(false);
          setPlanToRetire(null);
          setRetireError("");
        }}
        title="Deletion Failed"
      >
        <div style={{ marginBottom: "16px" }}>
          {retireError}
        </div>
        <div className="retire-plan-dialog-buttons" >
          <PrimaryButton
            text="OK"
            onClick={() => {
              setShowRetireErrorDialog(false);
              setPlanToRetire(null);
              setRetireError("");
            }}
          />
        </div>
      </Dialog>

      <TableContainer
        id="ag-grid-container"
        className={classNames("contracted-bundles-tab_grid", "ag-theme-balham")}
      >
        <TableHeader {...headerProps} />

        <TableContent
          data-testid="table-content"
          className="contracted-bundles_tab_grid-content"
          ref={tableContentRef}
        >
          <AgGridReact
            columnDefs={columns()}
            domLayout={TABLE_LAYOUT_OPTIONS.AUTO_HEIGHT}
            rowData={data}
            rowHeight={50}
            style={{ width: "100%" }}
            ref={gridRef}
            onGridReady={handleGridReady}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            suppressScrollOnNewData={true}
            getRowId={(params) => params.data.plan_name}
            rowClass="grid-row"
            frameworkComponents={{
              customNoRowsOverlay: CustomNoRowsOverlay,
            }}
            noRowsOverlayComponent="customNoRowsOverlay"
          />
        </TableContent>
      </TableContainer>
    </>
  );
}
